import React from "react";

export type SectionWidth = "narrow" | "prose" | "default" | "wide";
export type SectionSpacing = "normal" | "tight" | "hero";

export interface SectionProps {
  children: React.ReactNode;
  /**
   * Content measure: narrow/prose = max-w-3xl (prose pages center a ~65ch
   * article inside), default = max-w-5xl, wide = max-w-6xl.
   */
  width?: SectionWidth;
  /** Vertical rhythm: normal for page sections, tight for stacked follow-on
   *  sections, hero for a page's opening block. */
  spacing?: SectionSpacing;
  as?: "section" | "div" | "article" | "header";
  id?: string;
  className?: string;
}

const widths: Record<SectionWidth, string> = {
  narrow: "max-w-3xl",
  prose: "max-w-3xl",
  default: "max-w-5xl",
  wide: "max-w-6xl",
};

const spacings: Record<SectionSpacing, string> = {
  normal: "py-14 sm:py-20",
  tight: "py-8 sm:py-10",
  hero: "pt-20 pb-14",
};

/**
 * The single page-layout container: one horizontal padding scale and one
 * vertical rhythm for every page, replacing the ad-hoc py-12/16/20 +
 * max-w-3xl/4xl/5xl/6xl combinations that drifted across pages.
 */
const Section: React.FC<SectionProps> = ({
  children,
  width = "default",
  spacing = "normal",
  as: Tag = "section",
  id,
  className = "",
}) => (
  <Tag
    id={id}
    className={`mx-auto w-full px-4 sm:px-6 lg:px-8 ${widths[width]} ${spacings[spacing]} ${className}`}
  >
    {children}
  </Tag>
);

export default Section;
